import { useTheme } from '../../theme/useTheme'

/**
 * One small icon button, light or dark.
 *
 * Icon only, so the accessible name does the work: it says what pressing will
 * do rather than what the current state is, because a control you have to press
 * to understand is a guess. A live region announces the result, since the only
 * visual feedback is the icon swapping.
 */
export default function ThemeToggle({ tone = 'page' }: { tone?: 'page' | 'inverse' }) {
  const { theme, toggle } = useTheme()

  const next = theme === 'dark' ? 'light' : 'dark'
  const description = `Switch to ${next} theme`

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        aria-label={description}
        title={description}
        className={`inline-flex cursor-pointer items-center justify-center rounded p-2 ${
          tone === 'inverse'
            ? 'text-white hover:bg-white/15'
            : 'text-[var(--page-ink-muted)] hover:bg-[var(--page-canvas-sunken)] hover:text-[var(--page-ink)]'
        }`}
      >
        {theme === 'dark' ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
          </svg>
        )}
      </button>

      <span role="status" aria-live="polite" className="sr-only">
        {`${theme === 'dark' ? 'Dark' : 'Light'} theme`}
      </span>
    </>
  )
}
